import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import Header from '../components/Header';
import { BRAND } from '../data/brand';

const UPCOMING_DROP = {
  name: 'Secret Nº11',
  subtitle: 'The Midnight Secret',
  tagline: 'Some hours have no name.',
  description: 'A new formulation designed for the space between late night and early morning. Complex. Layered. Unlike anything in the current collection.',
  dropDate: new Date('2026-04-15T00:00:00'),
  category: 'Signature',
  tease: [
    { label: 'Profile', value: 'Rich, slow, evolving' },
    { label: 'Best For', value: 'The unnamed hours' },
    { label: 'Limited To', value: '500 units' },
  ],
};

const NewDropsPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [joined, setJoined] = useState(false);
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date();
      const diff = UPCOMING_DROP.dropDate - now;
      if (diff <= 0) {
        clearInterval(timer);
        return;
      }
      setCountdown({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60),
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const handleWaitlist = (e) => {
    e.preventDefault();
    if (email) {
      setJoined(true);
      setEmail('');
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-12 px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto text-center">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="font-sans text-xs tracking-[0.3em] uppercase text-zinc-500 mb-4"
          >
            Coming Soon
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-zinc-50 mb-6"
            data-testid="new-drops-heading"
          >
            New Drops
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="font-sans text-sm text-zinc-500 max-w-lg mx-auto leading-relaxed"
          >
            The next Secret is almost here. Get early access by joining the waitlist.
          </motion.p>
        </div>
      </section>

      {/* Countdown */}
      <section className="py-12 px-6 md:px-12 lg:px-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="max-w-3xl mx-auto"
        >
          <div className="grid grid-cols-4 gap-4 md:gap-8 text-center" data-testid="countdown">
            {[
              { value: countdown.days, label: 'Days' },
              { value: countdown.hours, label: 'Hours' },
              { value: countdown.minutes, label: 'Minutes' },
              { value: countdown.seconds, label: 'Seconds' },
            ].map((item) => (
              <div key={item.label} className="border border-white/5 py-6 md:py-8">
                <p className="font-serif text-3xl sm:text-4xl md:text-5xl text-zinc-50 mb-2">
                  {String(item.value).padStart(2, '0')}
                </p>
                <p className="font-sans text-[10px] tracking-[0.2em] uppercase text-zinc-600">{item.label}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Drop Details */}
      <section className="py-24 px-6 md:px-12 lg:px-24 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <p className="font-sans text-xs tracking-[0.2em] uppercase text-zinc-600 mb-3">{UPCOMING_DROP.category}</p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-zinc-50 mb-3">{UPCOMING_DROP.name}</h2>
            <p className="font-serif text-xl text-zinc-400 italic mb-6">{UPCOMING_DROP.subtitle}</p>
            <p className="font-sans text-sm text-zinc-500 max-w-xl mx-auto leading-relaxed">{UPCOMING_DROP.description}</p>
          </motion.div>

          <div className="grid grid-cols-3 gap-6 border-t border-white/5 pt-8">
            {UPCOMING_DROP.tease.map((item, index) => (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
                data-testid={`tease-${index}`}
              >
                <p className="font-sans text-[10px] tracking-[0.2em] uppercase text-zinc-600 mb-2">{item.label}</p>
                <p className="font-sans text-sm text-zinc-300">{item.value}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Waitlist */}
      <section className="py-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-md mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <Bell className="w-6 h-6 text-zinc-500 mx-auto mb-6" />
            <h3 className="font-serif text-2xl text-zinc-50 mb-3">Join the Waitlist</h3>
            <p className="font-sans text-xs text-zinc-500 mb-8">Be the first to know when {UPCOMING_DROP.name} drops.</p>

            {joined ? (
              <div data-testid="waitlist-success">
                <p className="font-sans text-sm text-zinc-300">You're on the list.</p>
                <p className="font-sans text-xs text-zinc-600 mt-1">We'll notify you before anyone else.</p>
              </div>
            ) : (
              <form onSubmit={handleWaitlist} className="flex gap-3">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  data-testid="waitlist-email"
                  className="flex-1 bg-transparent border-b border-white/10 py-3 font-sans text-sm text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:border-white/30 transition-colors"
                />
                <button
                  type="submit"
                  data-testid="waitlist-submit"
                  className="bg-white text-black px-6 py-3 font-sans text-xs tracking-[0.15em] uppercase hover:bg-zinc-200 transition-all duration-300"
                >
                  Notify Me
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/5">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6">
          <p className="font-serif text-xl text-zinc-50">Secret 13</p>
          <p className="font-sans text-xs text-zinc-600">{BRAND.legal.copyright} {BRAND.legal.membersOnly}</p>
        </div>
      </footer>
    </div>
  );
};

export default NewDropsPage;
